import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useApolloClient } from '@apollo/client';
import { Chart } from 'react-google-charts';
import { format, parseISO, startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { el } from 'date-fns/locale';
import { CellIntCustom, TableSubSimple } from 'motion-components';
import { useHandleError } from 'shared-pages/services';
import { gqlGetUsersLoginStats } from './graphql';
import { Filters, UsersStatsFilterValues, UsersStatsGroupBy } from './filters';

interface UsersStatsRow {
  periodBucket: string;
  platformOwner: number;
  collector: number;
  collectorDriver: number;
  wasteReceptionPoint: number;
  collectionPoint: number;
  contributor: number;
  total: number;
}

const initFilters: UsersStatsFilterValues = {
  fromDate: startOfMonth(new Date()),
  tillDate: endOfMonth(new Date()),
  groupBy: 'day',
};

function toQueryDate(date: Date, endOfPeriod = false): Date {
  return endOfPeriod ? endOfDay(date) : startOfDay(date);
}

function formatPeriodLabel(value: string | number | Date, groupBy: UsersStatsGroupBy): string {
  const dt = value instanceof Date ? value : parseISO(String(value));
  if (groupBy === 'hour') {
    return format(dt, 'eee dd/MM/yy HH:mm', { locale: el });
  }
  return format(dt, 'dd/MM/yyyy', { locale: el });
}

/** Συμπλήρωση κενών ωρών στο chart για συνεχή άξονα χρόνου */
function fillMissingHours(
  rows: UsersStatsRow[],
  groupBy: UsersStatsGroupBy,
): UsersStatsRow[] {
  if (groupBy !== 'hour' || !rows.length) return rows;

  const filled: UsersStatsRow[] = [];
  let lastHour = new Date(rows[0].periodBucket);

  rows.forEach((rec) => {
    const curHour = new Date(rec.periodBucket);
    const hourDiff = Math.abs(curHour.getTime() - lastHour.getTime()) / 3600000;

    if (hourDiff > 1) {
      for (let index = 1; index < hourDiff; index++) {
        const missingHour = new Date(lastHour.getTime() + index * 3600000);
        filled.push({
          periodBucket: missingHour.toISOString(),
          platformOwner: 0,
          collector: 0,
          collectorDriver: 0,
          wasteReceptionPoint: 0,
          collectionPoint: 0,
          contributor: 0,
          total: 0,
        });
        if (index > 48) break;
      }
    }

    filled.push(rec);
    lastHour = curHour;
  });

  return filled;
}

export const UsersStatsPage = () => {
  const client = useApolloClient();
  const { handleGraphqlError } = useHandleError('UsersStatsPage');

  const [queryParams, setQueryParams] = useState<UsersStatsFilterValues>(initFilters);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<UsersStatsRow[]>([]);
  const [chartData, setChartData] = useState<(string | number)[][] | undefined>();

  const fetchData = useCallback(
    async (params: UsersStatsFilterValues) => {
      setLoading(true);
      try {
        const ret = await client.query({
          query: gqlGetUsersLoginStats,
          variables: {
            fromDate: toQueryDate(params.fromDate).toISOString(),
            tillDate: toQueryDate(params.tillDate, true).toISOString(),
            groupBy: params.groupBy,
          },
          fetchPolicy: 'network-only',
        });

        const rows: UsersStatsRow[] = ret?.data?.getUsersLoginStats?.data ?? [];
        const filledRows = fillMissingHours(rows, params.groupBy);
        setData(filledRows);

        if (!filledRows.length) {
          setChartData(undefined);
        } else {
          const isHourly = params.groupBy === 'hour';
          const chData: (string | number | Date | { type: string; label: string })[][] = isHourly
            ? [
                [
                  'Ώρα',
                  'Όλοι',
                  'Πλατφόρμα',
                  'Συλλέκτες',
                  'Οδηγοί',
                  'Ανακυκλωτές',
                  'Σημεία Συλλογής',
                  'Εισφορείς',
                ],
              ]
            : [
                [
                  'Ημερομηνία',
                  'Όλοι',
                  'Πλατφόρμα',
                  'Συλλέκτες',
                  'Οδηγοί',
                  'Ανακυκλωτές',
                  'Σημεία Συλλογής',
                  'Εισφορείς',
                ],
              ];

          filledRows.forEach((rec) => {
            if (isHourly) {
              chData.push([
                formatPeriodLabel(rec.periodBucket, 'hour'),
                rec.total,
                rec.platformOwner,
                rec.collector,
                rec.collectorDriver,
                rec.wasteReceptionPoint,
                rec.collectionPoint,
                rec.contributor,
              ]);
            } else {
              chData.push([
                formatPeriodLabel(rec.periodBucket, 'day'),
                rec.total,
                rec.platformOwner,
                rec.collector,
                rec.collectorDriver,
                rec.wasteReceptionPoint,
                rec.collectionPoint,
                rec.contributor,
              ]);
            }
          });
          setChartData(chData as (string | number)[][]);
        }
      } catch (error) {
        setData([]);
        setChartData(undefined);
        handleGraphqlError('getUsersLoginStats', error);
      } finally {
        setLoading(false);
      }
    },
    [client, handleGraphqlError],
  );

  useEffect(() => {
    fetchData(initFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtersChanged = useCallback(
    (values: UsersStatsFilterValues) => {
      setQueryParams(values);
      fetchData(values);
    },
    [fetchData],
  );

  const isHourly = queryParams.groupBy === 'hour';
  const pageTitle = isHourly ? 'Χρήστες ανά Ώρα' : 'Χρήστες ανά Ημέρα';
  const pageDescription = isHourly
    ? 'Αθροίζονται για κάθε ώρα οι διαφορετικοί χρήστες (ανά τύπο) που έκαναν login στην πλατφόρμα. Κρατούνται στατιστικά για τους τελευταίους 2 μήνες.'
    : 'Αθροίζονται για κάθε ημέρα οι διαφορετικοί χρήστες (ανά τύπο) που έκαναν login στην πλατφόρμα. Κρατούνται στατιστικά για τους τελευταίους 2 μήνες.';

  const columns = useMemo(
    () => [
      {
        Header: isHourly ? 'Ώρα' : 'Ημέρα',
        accessor: 'periodBucket',
        Cell: ({ value }) => formatPeriodLabel(value, queryParams.groupBy),
      },
      {
        Header: 'Πλατφόρμα',
        accessor: 'platformOwner',
        Cell: CellIntCustom(7),
      },
      {
        Header: 'Συλλέκτες',
        accessor: 'collector',
        Cell: CellIntCustom(7),
      },
      {
        Header: 'Οδηγοί',
        accessor: 'collectorDriver',
        Cell: CellIntCustom(7),
      },
      {
        Header: 'Ανακυκλωτές',
        accessor: 'wasteReceptionPoint',
        Cell: CellIntCustom(7),
      },
      {
        Header: 'Σημεία Συλλογής',
        accessor: 'collectionPoint',
        Cell: CellIntCustom(7),
      },
      {
        Header: 'Εισφορείς',
        accessor: 'contributor',
        Cell: CellIntCustom(7),
      },
      {
        Header: 'Όλοι',
        accessor: 'total',
        Cell: CellIntCustom(7),
      },
    ],
    [isHourly, queryParams.groupBy],
  );

  return (
    <Card className="shadow">
      <Card.Header>
        <Card.Title className="m-0">{pageTitle}</Card.Title>
      </Card.Header>

      <p style={{ padding: '0.3rem 0.9rem' }}>{pageDescription}</p>

      <Filters onChange={filtersChanged} initialValues={queryParams} />

      <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
        {!loading ? (
          <div className="mt-4 mb-4">
            {data.length === 0 ? (
              <p className="text-center text-muted py-4 mb-0">
                Δεν βρέθηκαν Στοιχεία για το Χρονικό Διάστημα
              </p>
            ) : (
              <>
                {chartData && (
                  <div className="m-2 ml-4 mr-4">
                    <Chart
                      width="100%"
                      height="22rem"
                      chartType="AreaChart"
                      loader={<div>Loading Chart</div>}
                      data={chartData}
                      options={{
                        title: pageTitle,
                        hAxis: {
                          title: isHourly ? 'Ώρα' : 'Ημερομηνία',
                          titleTextStyle: { color: '#333' },
                        },
                        vAxis: { minValue: 0, title: 'Πλήθος Χρηστών' },
                        axisTitlesPosition: 'none',
                        pointsVisible: true,
                        isStacked: false,
                        legend: { position: 'bottom' },
                        chartArea: { width: '95%', height: '60%' },
                      }}
                    />
                  </div>
                )}

                <TableSubSimple columns={columns} data={data} />
              </>
            )}
          </div>
        ) : (
          <div style={{ height: '200px' }} />
        )}
      </LoadingOverlay>
    </Card>
  );
};
